import { useContext, useEffect, HTMLAttributes } from "react";
import { useIntl } from "react-intl";
import SummaryCard from "../summary-card";
import { OrganizationRole } from "../../../model/OrganizationRole";
import { Link } from "react-router-dom";
import { ModalKeys } from "../../pages/roles";
import { ButtonGroup } from "react-bootstrap";
import { sortToLatestByCreatedOrUpdated } from "../../../util/objectUtil";
import FieldList from "../../field-list";
import UIConfiguration from "../../../ui-configuration/configuration-provider";
import TooltipTrigger from "../../tooltip-trigger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TotalLabel } from "../summary-card/summary-card-view";
import RoleTypeBadge from "../../badges/role-type-badge";

interface RolesCardDOMProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {}
export interface RolesCardProps extends RolesCardDOMProps {
  isReady: boolean;
  roles?: OrganizationRole[] | null;
  onLoadRoles?: () => {};

  moreAction: () => void;
}

function RolesCard(props: RolesCardProps) {
  const { roles, onLoadRoles, moreAction, isReady } = props;
  const intl = useIntl();
  const { conf } = useContext(UIConfiguration);
  const rolesConf =
    conf.functionality && conf.functionality.roles
      ? conf.functionality.roles
      : ({} as any);
  const headerButton = {
    label: intl.formatMessage({
      defaultMessage: "View",
      description: "View button label",
    }),
    action: moreAction,
  };
  if (!onLoadRoles && roles === undefined) {
    throw new Error(
      "RolesCard: Required props missing. Either roles or onLoadRoles must be defined"
    );
  }
  const isRolesUndefined = roles === undefined;
  useEffect(() => {
    if (isRolesUndefined && onLoadRoles) {
      onLoadRoles();
    }
  }, [isRolesUndefined, onLoadRoles]);
  return (
    <SummaryCard
      onReloadData={() => {
        if (onLoadRoles) {
          onLoadRoles();
        }
      }}
      isReady={isReady}
      data-test-roles-card
      title={intl.formatMessage({
        defaultMessage: "Roles",
        description: "heading for the card",
      })}
      headerButton={headerButton}
      items={
        roles && roles.length
          ? roles.sort(sortToLatestByCreatedOrUpdated).map((r) => {
              const Wrapper = rolesConf.show ? Link : "span";
              const wrapperProps: any = {
                className:
                  "flex-shrink-1 d-flex justify-content-between align-items-center top-most mr-2",
              };
              if (rolesConf.show) {
                wrapperProps.to =
                  (("/roles/" + r.id) as string) + "/" + ModalKeys.show;
              }
              return (
                <>
                  <Wrapper {...wrapperProps}>
                    <TooltipTrigger
                      tipKey={"role_" + r.id}
                      tip={r.name}
                      placement={"top"}
                    >
                      <span className={"flex-shrink-1 mr-2 truncate"}>
                        {r.name}
                      </span>
                    </TooltipTrigger>
                    <RoleTypeBadge type={(r as any).type} />
                  </Wrapper>
                  {rolesConf.members && (
                    <ButtonGroup size={"sm"} className={"ml-auto"}>
                      <Link
                        className={"btn btn-theme-to-secondary"}
                        to={
                          (("/roles/" + r.id) as string) +
                          "/" +
                          ModalKeys.members
                        }
                      >
                        {intl.formatMessage({
                          defaultMessage: "Members",
                          description: "label for the members button",
                        })}
                      </Link>
                    </ButtonGroup>
                  )}
                </>
              );
            })
          : undefined
      }
      footer={
        <FieldList
          inline={true}
          fields={{
            total: {
              label: intl.formatMessage(TotalLabel),
              value: roles ? roles.length : 0,
            },
          }}
        />
      }
    >
      {roles && roles.length === 0 && (
        <div data-test-no-items className={"text-muted text-center py-4"}>
          <div className={"icon pb-1 mb-3"}>
            <span className="fa-layers fa-fw fa-2x">
              <FontAwesomeIcon
                icon="circle"
                mask={"database"}
                className={"opacity-50"}
                transform="shrink-0 right-5 down-5"
              />
              <FontAwesomeIcon icon="ban" transform="shrink-4 right-5 down-5" />
            </span>
          </div>
          <p className={"mb-0"}>
            {intl.formatMessage({
              defaultMessage: "No roles to show.",
              description: "display value for no roles",
            })}
          </p>
        </div>
      )}
    </SummaryCard>
  );
}
export default RolesCard;
